import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { dataForVSSubsUsersTable, sortingDate } from 'shared/helpers/utils';
import { Badge, Button, notification, Popconfirm, Skeleton, Spin } from 'antd';
import { viewerModel } from 'entities/viewer';
import { useAppDispatch, useAppSelector } from 'app/store';
import { RequestsSearch } from 'shared/types/types';
import { usePullSort } from 'shared/hooks';
import { RequestStatusType, SortCondition } from 'shared/enums';
import { SearchKeysNews } from 'shared/enums/news';
import { TVacationScheduleRequestPrepared } from 'shared/types/vacation-schedule.types';
import { ColumnsType } from 'antd/lib/table';
import { TableUserVsSubs } from 'features/tables/tableUserVsSubs';
import { getCurrentDivision } from 'entities/divisions/model';
import {
  getSubstitutionsSchedule,
  substitutionsScheduleSliceActions,
} from 'entities/vacationsSchedule/model/substitutionsScheduleSlice';
import { DayOffsStatusesMap, DayOffsStatusTypesMap } from 'shared/types/dayoff.types';
import Breadcrumbs from 'shared/components/breadcrumbs/Breadcrumbs';
import { vacationsScheduleSupervisorApi } from 'app/services/edpApiV1/supervisor/vacationsSchedule';
import { TabKeys } from 'features/divisions';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getVacationCheduleSupervisor } from 'entities/supervisor/vacationSchedule';
import { LoadingOutlined } from '@ant-design/icons';
import { returnErrorOrMessage } from 'shared/helpers/errors';
import { VacationModal } from '../vacationModal';
import style from './index.module.scss';

export const VacationsScheduleSubordinatesUser = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const { id } = useParams();
  const [query, setQuery] = useSearchParams();
  const me = useAppSelector(viewerModel.me.selectors.getViewerProfileInfo);
  const [sortInfo, sortTrigger] = usePullSort();
  const sortOption = sortInfo.value && sortInfo.value === SortCondition.ASCEND ? SortCondition.ASC : SortCondition.DESC;
  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });
  const { currentOption } = useAppSelector(getCurrentDivision);
  const { current } = useAppSelector(getSubstitutionsSchedule);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [requestId, setRequestId] = useState<number>(0);
  const [isActions, setIsActions] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [getVacationsScheduleDeputy] = vacationsScheduleSupervisorApi.useLazyVacationsScheduleDeputyQuery();
  const [getVacationsScheduleActing] = vacationsScheduleSupervisorApi.useLazyVacationsScheduleActingQuery();
  const [getVacationsScheduleSupervisor] = vacationsScheduleSupervisorApi.useLazyVacationsScheduleSupervisorQuery();

  const { data, pagination, loading, success, error } = useAppSelector(getVacationCheduleSupervisor);

  useEffect(() => {
    if (!current) {
      return;
    }
    if (current.requests?.[0] && current.requests?.[0].status.type === RequestStatusType.consideredBySupervisor) {
      setIsActions(true);
      return;
    }
    setIsActions(false);
  }, [current]);

  const refetch = useCallback(async () => {
    if (!currentOption) {
      return;
    }
    try {
      switch (currentOption.type) {
        case TabKeys.ACTING:
          await getVacationsScheduleActing({
            divisionId: currentOption.value,
            page: Number(query.get('page')),
            size: Number(query.get('size')),
          }).unwrap();
          break;
        case TabKeys.DEPUTY:
          await getVacationsScheduleDeputy({
            page: Number(query.get('page')),
            size: Number(query.get('size')),
            divisionId: currentOption.value,
          }).unwrap();
          break;
        case TabKeys.SUPERVISOR:
          await getVacationsScheduleSupervisor({
            page: Number(query.get('page')),
            size: Number(query.get('size')),
          }).unwrap();
          break;
      }
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
    }
  }, [currentOption, query]);

  const breadCrumbs = useMemo(() => {
    return [
      { name: 'Планирование отпусков сотрудников', link: '/vacation-schedule-subordinates' },
      {
        name: 'Отпуска сотрудника',
        link: `/vacation-schedule-subordinates/user/${id}?page=${Number(query.get('page'))}&size=${Number(
          query.get('size'),
        )}`,
      },
    ];
  }, [id, query]);

  useEffect(() => {
    refetch();
  }, [id, refetch, currentOption]);

  useEffect(() => {
    if (data && id) {
      const target = data.find(elem => elem.id === Number(id));
      if (!target) {
        return;
      }
      dispatch(substitutionsScheduleSliceActions.setCurrentSchedule(target));
    }
  }, [data]);

  const [approveVacationsScheduleDeputy, { isLoading: isDeputyApproveLoading }] =
    vacationsScheduleSupervisorApi.useVacationScheduleApproveDeputyMutation();
  const [approveVacationsScheduleActing, { isLoading: isActingApproveLoading }] =
    vacationsScheduleSupervisorApi.useVacationScheduleApproveActingMutation();
  const [approveVacationsScheduleSupervisor, { isLoading: isSupervisorApproveLoading }] =
    vacationsScheduleSupervisorApi.useVacationScheduleApproveSupervisorMutation();

  const [rejectVacationsScheduleDeputy, { isLoading: isDeputyRejectLoading }] =
    vacationsScheduleSupervisorApi.useVacationScheduleRejectDeputyMutation();
  const [rejectVacationsScheduleActing, { isLoading: isActingRejectLoading }] =
    vacationsScheduleSupervisorApi.useVacationScheduleRejectActingMutation();
  const [rejectVacationsScheduleSupervisor, { isLoading: isSupervisorRejectLoading }] =
    vacationsScheduleSupervisorApi.useVacationScheduleRejectSupervisorMutation();

  const handleSubmitRejectModal = async (comment: string) => {
    if (!currentOption) {
      return;
    }
    try {
      switch (currentOption.type) {
        case TabKeys.ACTING:
          await rejectVacationsScheduleActing({
            divisionId: currentOption.value,
            comment,
            employeeId: Number(current?.id),
            requestId,
          }).unwrap();
          break;
        case TabKeys.DEPUTY:
          await rejectVacationsScheduleDeputy({
            divisionId: currentOption.value,
            comment,
            employeeId: Number(current?.id),
            requestId,
          }).unwrap();
          break;
        case TabKeys.SUPERVISOR:
          await rejectVacationsScheduleSupervisor({
            comment,
            employeeId: Number(current?.id),
            requestId,
          }).unwrap();
          break;
      }
      notification.success({ message: 'Запланированный отпуск отклонен' });
      navigate('/vacation-schedule-subordinates');
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
    }
  };

  const reject = (id: number) => {
    setRequestId(id);
    setOpenModal(true);
  };

  const approve = async () => {
    if (!currentOption) {
      return;
    }
    try {
      switch (currentOption.type) {
        case TabKeys.ACTING:
          await approveVacationsScheduleActing({
            divisionId: currentOption.value,
            employeeIds: [Number(current?.id)],
          }).unwrap();
          break;
        case TabKeys.DEPUTY:
          await approveVacationsScheduleDeputy({
            divisionId: currentOption.value,
            employeeIds: [Number(current?.id)],
          }).unwrap();
          break;
        case TabKeys.SUPERVISOR:
          await approveVacationsScheduleSupervisor({ employeeIds: [Number(current?.id)] }).unwrap();
          break;
      }
      notification.success({ message: 'Запланированный отпуск согласован' });
      navigate('/vacation-schedule-subordinates');
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
    }
  };

  const columns: ColumnsType<TVacationScheduleRequestPrepared> = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },

    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Вид отпуска',
      dataIndex: 'vacationType',
      key: 'vacationType',
    },
    {
      title: 'Дата начала',
      dataIndex: 'startedAt',
      key: 'startedAt',
      sorter: (a, b) => sortingDate(a.startedAt, b.startedAt),
    },
    {
      title: 'Дата окончания',
      dataIndex: 'endedAt',
      key: 'endedAt',
      sorter: (a, b) => sortingDate(a.endedAt, b.endedAt),
    },
    {
      title: 'Дни',
      dataIndex: 'amountDays',
      key: 'amountDays',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: { name: string; type: RequestStatusType }) => (
        <span>
          <Badge className={style.badge} status={DayOffsStatusTypesMap[status.type]} />
          {DayOffsStatusesMap[status.type]}
        </span>
      ),
    },
    {
      title: 'Действие',
      dataIndex: 'id',
      key: 'action',
      render: (id, record) => {
        return (
          <>
            {isDeputyRejectLoading || isActingRejectLoading || isSupervisorRejectLoading ? (
              <Spin indicator={<LoadingOutlined spin />} />
            ) : (
              <Popconfirm placement="leftTop" title="Вы уверены?" onConfirm={() => reject(id)}>
                <a className={style.reject}>Отклонить</a>
              </Popconfirm>
            )}
          </>
        );
      },
    },
  ];
  const columnsWOActions: ColumnsType<TVacationScheduleRequestPrepared> = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },

    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Вид отпуска',
      dataIndex: 'vacationType',
      key: 'vacationType',
    },
    {
      title: 'Дата начала',
      dataIndex: 'startedAt',
      key: 'startedAt',
      sorter: (a, b) => sortingDate(a.startedAt, b.startedAt),
    },
    {
      title: 'Дата окончания',
      dataIndex: 'endedAt',
      key: 'endedAt',
      sorter: (a, b) => sortingDate(a.endedAt, b.endedAt),
    },
    {
      title: 'Дни',
      dataIndex: 'amountDays',
      key: 'amountDays',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: { name: string; type: RequestStatusType }) => (
        <span>
          <Badge className={style.badge} status={DayOffsStatusTypesMap[status.type]} />
          {DayOffsStatusesMap[status.type]}
        </span>
      ),
    },
  ];

  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    }
  };

  const onRowClick = (data: TVacationScheduleRequestPrepared) => {};

  return (
    <>
      {!current ? (
        <Skeleton active />
      ) : (
        <>
          <VacationModal
            id="rejectVSSupervisorModal"
            openModal={openModal}
            setOpenModal={setOpenModal}
            handleSubmit={handleSubmitRejectModal}
          />
          {columns && (
            <>
              <div className={style.buttons}>
                <h3 className={style.title}>
                  <Breadcrumbs breadCrumbs={breadCrumbs} />
                </h3>
                <Popconfirm title={'Вы уверены?'} onConfirm={() => approve()}>
                  <Button
                    type="primary"
                    disabled={!(current.requests?.length || current.scheduleRequests?.length) || !isActions}
                    loading={isDeputyApproveLoading || isActingApproveLoading || isSupervisorApproveLoading}
                  >
                    Согласовать
                  </Button>
                </Popconfirm>
              </div>
              <TableUserVsSubs
                dataSource={dataForVSSubsUsersTable(
                  current.requests || current.scheduleRequests || [],
                  `${current.lastName} ${current.name} ${current.middleName}`,
                )}
                columns={isActions ? columns : columnsWOActions}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                currentSize={currentSize}
                setCurrentSize={setCurrentSize}
                total={10}
                onSearch={onSearch}
                onRowClick={onRowClick}
              />
            </>
          )}
        </>
      )}
    </>
  );
};
